<template>
  <v-app>
    <v-container fluid class="fill-height pa-0" style="background: linear-gradient(135deg, #EFEBE9 0%, #D7CCC8 100%);">
      <v-row no-gutters class="fill-height" align="center" justify="center">
        <v-col cols="12" sm="8" md="8" lg="6" xl="4">
          <v-card class="elevation-12 rounded-xl" style="overflow: hidden;">
            <!-- Header with centered title -->
            <v-card-title class="primary white--text pa-6 d-flex flex-column align-center justify-center text-center">
              <h2 class="font-weight-light mb-1">Iniciar Sesión</h2>
              <p class="mb-0 body-2">Accede a tu cuenta del Centro Médico</p>
            </v-card-title>

            <v-card-text class="pa-6">
              <form @submit.prevent="validateLoginForm">
                <v-text-field
                  v-model="authinfo.federation_acct"
                  label="Correo Electrónico"
                  type="email"
                  prepend-inner-icon="mdi-email"
                  outlined
                  class="mb-3"
                  :error-messages="$v.authinfo.federation_acct.$error ? 'Ingrese un email válido' : ''"
                  @blur="$v.authinfo.federation_acct.$touch()"
                ></v-text-field>

                <v-text-field
                  v-model="authinfo.password"
                  label="Contraseña"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  outlined
                  class="mb-4"
                  :error-messages="$v.authinfo.password.$error ? 'Contraseña debe tener al menos 6 caracteres' : ''"
                  @click:append="showPassword = !showPassword"
                  @blur="$v.authinfo.password.$touch()"
                ></v-text-field>

                <v-btn
                  type="submit"
                  color="primary"
                  large
                  block
                  class="mb-4 rounded-lg"
                  :loading="dialog"
                  :disabled="$v.$invalid"
                >
                  <v-icon left>mdi-login</v-icon>
                  Iniciar Sesión
                </v-btn>
              </form>

              <v-divider class="mb-4"></v-divider>

              <!-- Enlaces de ayuda -->
              <div class="text-center">
                <v-btn
                  text
                  color="primary"
                  small
                  class="mb-2"
                  @click="forgot"
                >
                  <v-icon left small>mdi-help-circle</v-icon>
                  ¿Olvidaste tu contraseña?
                </v-btn>
                <br>
                <v-btn
                  text
                  color="secondary"
                  small
                  @click="resend"
                >
                  <v-icon left small>mdi-email-send</v-icon>
                  Reenviar email de confirmación
                </v-btn>
              </div>
            </v-card-text>

            <!-- Footer con enlace a registro -->
            <v-card-actions class="pa-6 pt-0">
              <v-spacer></v-spacer>
              <div class="text-center w-100">
                <p class="body-2 grey--text mb-2">¿No tienes cuenta?</p>
                <v-btn
                  outlined
                  color="primary"
                  :to="'/signup'"
                  class="rounded-lg"
                >
                  <v-icon left>mdi-account-plus</v-icon>
                  Crear Cuenta
                </v-btn>
              </div>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading Dialog -->
      <v-dialog
        v-model="dialog"
        persistent
        width="300"
      >
        <v-card class="rounded-lg">
          <v-card-text class="text-center pa-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            ></v-progress-circular>
            <p class="body-1 mb-0">Iniciando sesión...</p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { loginConPerfil } from '@/services/auth'
import { getPersonByEmail } from '@/services/monolito'
import { SUPERUSER_ROLE } from '@/store'
import { verComo } from '@/services/superuser'

export default {
    name: 'Login',
    mixins: [validationMixin],
    data() {
        return {
            authinfo: {
                federation_acct: '',
                password: ''
            },
            dialog: false,
            showPassword: false
        }
    },
    validations: {
        authinfo: {
            federation_acct: { required },
            password: { required, minLength: minLength(6) }
        }
    },
    methods: {
        async validateLoginForm() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                return
            }
            
            this.dialog = true
            try {
                const { tokens, perfil } = await loginConPerfil({
                    username: this.authinfo.federation_acct,
                    password: this.authinfo.password
                }, this.$store)

                // Superusuario: no necesita paciente en el monolito, arranca como secretaría
                if (Array.isArray(perfil.roles) && perfil.roles.includes(SUPERUSER_ROLE)) {
                    verComo(this.$store, 'secretaria')
                    return
                }
                
                // Verificar si el usuario existe en el monolito
                const email = perfil.email || this.authinfo.federation_acct;
                await this.checkAndRedirect(email, perfil);
                
            } catch (error) {
                console.error('Error en login:', error)
                this.dialog = false
                
                // Mostrar mensaje de error al usuario
                if (error.response && error.response.status === 401) {
                    alert('Credenciales incorrectas. Por favor verifique su email y contraseña.');
                } else if (error.response && error.response.status >= 500) {
                    alert('Error del servidor. Por favor intente nuevamente más tarde.');
                } else {
                    alert('Error de conexión. Por favor verifique su conexión a internet.');
                }
            }
        },
        
        async checkAndRedirect(email, perfil) {
            try {
                
                // Intentar obtener el paciente del monolito
                const paciente = await getPersonByEmail(email);
                
                if (paciente === null) {
                    // Usuario no existe en monolito → redirigir a completar perfil
                    this.dialog = false;
                    this.$router.push('/completar-perfil');
                } else {
                    // Usuario existe → redirigir según rol
                    
                    // Extraer datos del paciente para el store
                    // La estructura puede variar según el endpoint
                    let pacienteData = paciente;
                    let telefono = '';
                    let dni = '';
                    let name = '';
                    
                    // Si es un array directo
                    if (Array.isArray(paciente) && paciente.length > 0) {
                        pacienteData = paciente[0];
                    }
                    // Si tiene propiedad data que es array
                    else if (paciente.data && Array.isArray(paciente.data) && paciente.data.length > 0) {
                        pacienteData = paciente.data[0];
                    }
                    
                    // Extraer party si existe
                    const party = pacienteData.party || pacienteData;
                    
                    // Buscar teléfono en los contactos (puede ser 'contacto' o 'contact_mechanisms')
                    const contactos = party.contacto || party.contact_mechanisms || party.contacts || [];
                    
                    if (Array.isArray(contactos)) {
                        const mobileContact = contactos.find(function(c) { 
                            return c.type === 'mobile' || c.contact_type === 'mobile'; 
                        });
                        const phoneContact = contactos.find(function(c) { 
                            return c.type === 'phone' || c.contact_type === 'phone'; 
                        });
                        telefono = (mobileContact && (mobileContact.value || mobileContact.contact_value)) || 
                                   (phoneContact && (phoneContact.value || phoneContact.contact_value)) || '';
                    }
                    
                    // Si no hay contactos en array, buscar directamente
                    if (!telefono && (party.phone || party.telefono || party.mobile)) {
                        telefono = party.phone || party.telefono || party.mobile || '';
                    }
                    
                    // Extraer DNI
                    dni = party.dni || party.ref || party.code || '';
                    name = party.name || '';
                    const patientId = pacienteData.patient_id || pacienteData.paciente_id || pacienteData.id || perfil.patient_id || perfil.paciente_id || null;
                    
                    // Guardar en credentials (para checkPaciente)
                    this.$store.commit('set_credentials', {
                        federation_acct: email,
                        password: '',
                        name: name,
                        dni: dni,
                        telefono: telefono,
                        role: perfil.role || 'patient'
                    });
                    
                    // Guardar datos del paciente en el store
                    this.$store.commit('set_user_profile', {
                        ...perfil,
                        ...pacienteData,
                      patient_id: patientId,
                        telefono: telefono,
                        dni: dni,
                        perfilCompleto: true
                    });
                    
                    this.redirectBasedOnRole(perfil);
                }
            } catch (error) {
                console.error('Error verificando paciente en monolito:', error);
                
                // Si hay error de red o 401, probablemente el monolito no está disponible
                // En ese caso, redirigir a completar perfil para que el usuario pueda crearse
                if (error.response && error.response.status === 401) {
                    console.warn('Error 401 al verificar paciente - posible problema de autenticación con monolito');
                    // Aún así, intentar redirigir a completar perfil
                    this.dialog = false;
                    this.$router.push('/completar-perfil');
                } else {
                    // Para otros errores, continuar con el flujo normal
                    console.warn('Error al verificar paciente, continuando con flujo normal');
                    this.redirectBasedOnRole(perfil);
                }
            }
        },
        
        redirectBasedOnRole(perfil) {
            // Extraer el rol considerando varias estructuras posibles
            let role = perfil?.role || 
                       (perfil?.roles && perfil.roles[0]) || 
                       this.$store.state.user_profile?.role || 
                       (this.$store.state.user_profile?.roles && this.$store.state.user_profile.roles[0]) || 
                       this.$store.state.credentials?.role;

            if (role) {
                role = role.toLowerCase();
            }

            const isSecretary = role === 'secretary' || role === 'secretaria' || role === 'admin';
            const isDoctor = role === 'doctor' || role === 'medico' || role === 'health_professional';

            // Determinar la ruta por defecto según el rol del usuario
            let targetRoute = '/turnos';
            if (isSecretary) {
                targetRoute = '/turnos-secretaria';
            } else if (isDoctor) {
                targetRoute = '/agenda';
            }

            // Verificar si hay una redirección pendiente en la URL
            const redirectPath = this.$route.query.redirect;
            if (redirectPath && redirectPath !== '/login') {
                const isPatientOnlyRoute = redirectPath === '/' || redirectPath === '/turnos' || redirectPath === '/calendar' || redirectPath === '/turnospaciente' || redirectPath === '/nuevoturno';

                if (isSecretary && isPatientOnlyRoute) {
                    targetRoute = '/turnos-secretaria';
                } else if (isDoctor && isPatientOnlyRoute) {
                    targetRoute = '/agenda';
                } else {
                    targetRoute = redirectPath;
                }
            }
            
            this.$router.push(targetRoute).catch(err => {
                const isExpectedNavigationError =
                    err && (
                        err.name === 'NavigationDuplicated' ||
                        (err.message && err.message.includes('Redirected when going from'))
                    );

                if (!isExpectedNavigationError) {
                    console.error('Error de navegación:', err);
                }
            });
        },
        forgot() {
            this.$router.push('/forgot')
        },
        resend() {
            this.$router.push('/resend')
        }
    }
}
</script>

<style scoped>
.button-link {
    color: #8D4E3C;
    text-decoration: none;
    cursor: pointer;
}

.button-link:hover {
    text-decoration: underline;
}
</style>